import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";

import { Action as DropdownActionType } from "@client/components/ui/DropdownAction";
import { ItemComponentProps, ListItem } from "@typings/List";

import FilterListFilter from "../Filter";
import FilterListList from "../List";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
`;

type DictItem = { value: string; label: string };
type Props = {
  items: any[];
  dropdownActions?: DropdownActionType[];
  onSelectedItemsChange?: (items: any[]) => void;
  loading: boolean;
  onReloadButtonClick: () => void;
  onItemClick: (item: any) => void;
  selectionLabel: string;
  renderItemComponent: (componentProps: ItemComponentProps) => React.ReactNode;
  itemFilterFunction: (
    item: any,
    filterStatus?: string | null,
    filterState?: string,
  ) => boolean;
  filterItems: DictItem[];
  emptyListImage?: string | null;
  emptyListMessage?: string;
  emptyListExtraMessage?: string;
  emptyListButtonLabel?: string;
  onEmptyListButtonClick?: () => void;
  customFilterComponent?: React.ReactNode;
};
type State = {
  items: any[];
  filterStatus: string;
  filterText: string;
  selectedItems: any[];
  selectAllSelected: boolean;
};
@observer
class ListWrapper extends React.Component<Props, State> {
  state: State = {
    items: [],
    filterStatus: "all",
    filterText: "",
    selectedItems: [],
    selectAllSelected: false,
  };

  UNSAFE_componentWillMount() {
    this.setState({ items: this.props.items });
  }

  UNSAFE_componentWillReceiveProps(props: Props) {
    if (props.items.length !== this.props.items.length) {
      this.setState({
        items: props.items,
        filterStatus: "all",
        filterText: "",
        selectedItems: [],
      });
      this.props.onSelectedItemsChange && this.props.onSelectedItemsChange([]);
      return;
    }

    this.setState({ items: this.filterItems(props.items) });
  }

  handleFilterItemClick(item: DictItem) {
    const items = this.filterItems(this.props.items, item.value);
    const selectedItems = this.state.selectedItems.filter(selItem => {
      if (items.find(i => selItem.id === i.id)) {
        return true;
      }
      return false;
    });

    const selectAllSelected =
      selectedItems.length > 0 && selectedItems.length === items.length;
    this.setState(
      {
        selectedItems,
        selectAllSelected,
        filterStatus: item.value,
        items,
      },
      () => {
        this.props.onSelectedItemsChange &&
          this.props.onSelectedItemsChange(selectedItems);
      },
    );
  }

  handleSearchChange(text: string) {
    this.setState({
      filterText: text,
      items: this.filterItems(this.props.items, null, text),
    });
  }

  handleItemSelectedChange(item: ListItem, selected: boolean) {
    const items = this.state.selectedItems.slice(0);
    const selectedItems = items.filter(i => item.id !== i.id) || [];

    if (selected) {
      selectedItems.push(item);
    }

    this.setState({ selectedItems, selectAllSelected: false }, () => {
      this.props.onSelectedItemsChange &&
        this.props.onSelectedItemsChange(selectedItems);
    });
  }

  handleSelectAllChange(selected: boolean) {
    let selectedItems: any[] = [];
    if (selected) {
      selectedItems = this.state.items.slice(0);
    }

    this.setState({ selectedItems, selectAllSelected: selected }, () => {
      this.props.onSelectedItemsChange &&
        this.props.onSelectedItemsChange(selectedItems);
    });
  }

  filterItems(
    items: ListItem[],
    filterStatus?: string | null,
    filterText?: string,
  ): ListItem[] {
    filterStatus = filterStatus || this.state.filterStatus;
    filterText =
      typeof filterText === "undefined" ? this.state.filterText : filterText;
    const filteredItems = items.filter(item =>
      this.props.itemFilterFunction(item, filterStatus, filterText),
    );
    return filteredItems;
  }

  render() {
    return (
      <Wrapper>
        <FilterListFilter
          onFilterItemClick={item => {
            this.handleFilterItemClick(item);
          }}
          selectedValue={this.state.filterStatus}
          onReloadButtonClick={this.props.onReloadButtonClick}
          onSearchChange={text => {
            this.handleSearchChange(text);
          }}
          searchValue={this.state.filterText}
          onSelectAllChange={selected => {
            this.handleSelectAllChange(selected);
          }}
          selectAllSelected={this.state.selectAllSelected}
          customFilterComponent={this.props.customFilterComponent}
          selectionInfo={{
            selected: this.state.selectedItems.length,
            total: this.state.items.length,
            label: this.props.selectionLabel,
          }}
          items={this.props.filterItems}
          dropdownActions={this.props.dropdownActions || []}
        />
        <FilterListList
          loading={this.props.loading}
          items={this.state.items}
          selectionEnabled={!!this.props.dropdownActions}
          selectedItems={this.state.selectedItems}
          onSelectedChange={(item, selected) => {
            this.handleItemSelectedChange(item, selected);
          }}
          onItemClick={this.props.onItemClick}
          renderItemComponent={this.props.renderItemComponent}
          showEmptyList={
            this.state.items.length === 0 &&
            this.state.filterStatus === "all" &&
            this.state.filterText === ""
          }
          emptyListImage={this.props.emptyListImage || null}
          emptyListMessage={this.props.emptyListMessage}
          emptyListExtraMessage={this.props.emptyListExtraMessage}
          emptyListButtonLabel={this.props.emptyListButtonLabel}
          onEmptyListButtonClick={this.props.onEmptyListButtonClick}
        />
      </Wrapper>
    );
  }
}

export default ListWrapper;
